import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend,
} from 'recharts';
import { fetchSupplierInfo, listSupplierProducts } from '../services/supplierService';
import { exportCockpitHtml, exportCockpitPdf, exportCockpitBackend, buildChartImages } from '../utils/cockpitExport';
import './SupplierCockpit.css';

const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';
const STATUS_COLORS = { red: '#D64545', orange: '#D98323', green: '#1D8A5E', grey: '#6B8299' };
const BAR_COLORS = ['#1A8AAF', '#0D2B3E', '#2FA4C7', '#1D8A5E', '#D98323', '#6B8299', '#8E5AA8', '#3E7CB1', '#C74F4F', '#4CA88A'];

const upper = (s) => String(s || '').toUpperCase().trim();
const firstDate = (r) => r.reference_date || r.updated_at || r.created_at || null;
const yearOf = (d) => { const t = new Date(d); return isNaN(t) ? null : t.getFullYear(); };
const fmtDate = (d) => { const t = new Date(d); return isNaN(t) ? '—' : t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };

function topCounts(items, keyFn, limit = 10) {
  const m = new Map();
  for (const it of items) { const k = keyFn(it); if (!k) continue; m.set(k, (m.get(k) || 0) + 1); }
  return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([name, value]) => ({ name, value }));
}

/* Reusable table with a per-column filter row aligned to the headers.
   Optional pagination (matches the /supplier-products pager). */
const PAGE_SIZE = 25;
function FilterableTable({ columns, rows, maxRows = 300, emptyText = 'No rows.', paginate = false, noun = 'rows' }) {
  const [f, setF] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const filtered = useMemo(() => rows.filter((r) => columns.every((c) => {
    const val = f[c.key];
    if (val === undefined || val === '') return true;
    const cell = String(r[c.key] ?? '').toLowerCase();
    return c.type === 'select' ? cell === String(val).toLowerCase() : cell.includes(String(val).toLowerCase());
  })), [rows, f, columns]);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPage(1); };

  const effective = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(filtered.length / effective));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * effective;
  const end = Math.min(start + effective, filtered.length);
  const bodyRows = paginate ? filtered.slice(start, end) : filtered.slice(0, maxRows);
  const btn = (extra = {}) => ({ fontSize: 12, border: '1px solid var(--teal)', background: 'var(--card)', color: 'var(--teal)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', ...extra });

  return (
    <>
      <div className="cok-table-wrap" style={{ maxHeight: paginate ? undefined : 540, overflowY: paginate ? 'visible' : 'auto' }}>
        <table className="cok-table">
          <thead>
            <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
            <tr className="cok-filter-row">
              {columns.map((c) => (
                <th key={c.key}>
                  {c.noFilter ? null : c.type === 'select' ? (
                    <select value={f[c.key] || ''} onChange={(e) => set(c.key, e.target.value)}>
                      <option value="">All</option>
                      {(c.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input placeholder="Filter…" value={f[c.key] || ''} onChange={(e) => set(c.key, e.target.value)} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((r, i) => (
              <tr key={i}>{columns.map((c) => <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <div className="cok-muted" style={{ marginTop: 8 }}>{emptyText}</div>}

      {paginate && filtered.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 2px 0', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => { setPageSize((ps) => (ps < filtered.length ? filtered.length : PAGE_SIZE)); setPage(1); }} style={btn()}>
            {pageSize < filtered.length ? 'Show All' : `Show ${PAGE_SIZE}`}
          </button>
          <nav aria-label="pagination" style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" disabled={safePage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={btn({ color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', borderColor: safePage === 1 ? '#dee2e6' : 'var(--teal)', cursor: safePage === 1 ? 'not-allowed' : 'pointer' })}>Previous</button>
            {(() => {
              const win = 5;
              let s = Math.max(1, safePage - Math.floor(win / 2));
              const e = Math.min(pageCount, s + win - 1);
              s = Math.max(1, e - win + 1);
              const nums = []; for (let i = s; i <= e; i++) nums.push(i);
              return nums.map((n) => (
                <button key={n} type="button" onClick={() => setPage(n)} style={btn({ color: safePage === n ? '#fff' : 'var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', fontWeight: safePage === n ? 600 : 400 })}>{n}</button>
              ));
            })()}
            <button type="button" disabled={safePage === pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} style={btn({ color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', borderColor: safePage === pageCount ? '#dee2e6' : 'var(--teal)', cursor: safePage === pageCount ? 'not-allowed' : 'pointer' })}>Next</button>
          </nav>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
            Showing {filtered.length === 0 ? 0 : start + 1}–{end} of {filtered.length.toLocaleString('en-US')} {noun}
          </span>
        </div>
      )}
      {!paginate && filtered.length > maxRows && <div className="cok-muted" style={{ marginTop: 8 }}>Showing first {maxRows} of {filtered.length}. Use the column filters to narrow down.</div>}
    </>
  );
}

function AiComment({ text, loading }) {
  return (
    <div className="cok-ai">
      <div className="cok-ai-head"><span className="cok-ai-badge">✦</span>Nova insights</div>
      <div className={`cok-ai-body${loading ? ' loading' : ''}`}>
        {loading ? 'Generating insights from the data…' : (text || 'No insight available for this section.')}
      </div>
    </div>
  );
}

export default function SupplierCockpit() {
  const { name } = useParams();
  const supplierName = decodeURIComponent(name || '');
  const navigate = useNavigate();
  const { instance: msalInstance, accounts } = useMsal();

  const [info, setInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [switchMap, setSwitchMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState('');
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [newsStatus, setNewsStatus] = useState('loading'); // loading | ready
  const [phase, setPhase] = useState('intro'); // intro | loading | fading | done
  const [progress, setProgress] = useState(3);

  const acquireToken = useCallback(async () => {
    try {
      if (!accounts || !accounts.length) return null;
      const r = await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account: accounts[0] });
      return r.accessToken;
    } catch { return null; }
  }, [msalInstance, accounts]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const [inf, prods] = await Promise.all([
          fetchSupplierInfo(supplierName).catch(() => null),
          listSupplierProducts(supplierName).catch(() => []),
        ]);
        if (!alive) return;
        setInfo(inf || null);
        setProducts(Array.isArray(prods) ? prods : []);
      } catch {
        if (alive) setErr('Could not load supplier data.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [supplierName]);

  useEffect(() => {
    const mols = [...new Set(products.map((r) => upper(r.inn || r.molecule_name)).filter(Boolean))];
    if (!mols.length) { setSwitchMap({}); return; }
    let alive = true;
    (async () => {
      try {
        const token = await acquireToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/market-intelligence/switch-status`, {
          method: 'POST', headers, body: JSON.stringify({ molecules: mols }),
        });
        if (res.ok && alive) {
          const raw = await res.json();
          const norm = {};
          for (const [k, v] of Object.entries(raw)) norm[upper(k)] = v;
          setSwitchMap(norm);
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [products, acquireToken]);

  const statusOf = useCallback((r) => switchMap[upper(r.inn || r.molecule_name)] || 'green', [switchMap]);

  const formOptions = useMemo(() => [...new Set(products.map((r) => r.dosage_form).filter(Boolean))].sort(), [products]);
  const atcOptions = useMemo(() => [...new Set(products.map((r) => r.atc_category).filter(Boolean))].sort(), [products]);

  // KPIs (whole supplier)
  const kpis = useMemo(() => {
    const molecules = new Set(products.map((r) => upper(r.inn || r.molecule_name)).filter(Boolean));
    let red = 0, orange = 0;
    for (const mol of molecules) { const st = switchMap[mol]; if (st === 'red') red++; else if (st === 'orange') orange++; }
    const nl = products.filter((r) => String(r.nl_market_available || '').toLowerCase() === 'yes').length;
    return { products: products.length, molecules: molecules.size, forms: new Set(products.map((r) => r.dosage_form).filter(Boolean)).size, nl, red, orange };
  }, [products, switchMap]);

  // chart data (whole supplier)
  const byForm = useMemo(() => topCounts(products, (r) => r.dosage_form, 12), [products]);
  const byAtc = useMemo(() => topCounts(products, (r) => r.atc_category, 10), [products]);
  const byTA = useMemo(() => topCounts(products, (r) => r.atc_classification_name, 12), [products]);
  const nlGap = useMemo(() => {
    let yes = 0, no = 0, unk = 0;
    for (const r of products) { const v = String(r.nl_market_available || '').toLowerCase(); if (v === 'yes') yes++; else if (v === 'no') no++; else unk++; }
    return [{ name: 'NL available', key: 'green', value: yes }, { name: 'Not available', key: 'red', value: no }, { name: 'Unknown', key: 'grey', value: unk }].filter((d) => d.value > 0);
  }, [products]);
  const trend = useMemo(() => {
    const m = new Map();
    for (const r of products) { const y = yearOf(firstDate(r)); if (y) m.set(y, (m.get(y) || 0) + 1); }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([year, value]) => ({ year: String(year), value }));
  }, [products]);
  const cumulative = useMemo(() => { let s = 0; return trend.map((p) => ({ year: p.year, cumulative: (s += p.value) })); }, [trend]);
  const predictive = useMemo(() => {
    const pts = trend;
    const data = pts.map((p) => ({ year: p.year, actual: p.value }));
    if (pts.length < 2) return { data, hasForecast: false };
    const n = pts.length, xs = pts.map((_, i) => i), ys = pts.map((p) => p.value);
    const mx = xs.reduce((a, b) => a + b, 0) / n, my = ys.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0; for (let i = 0; i < n; i++) { num += (xs[i] - mx) * (ys[i] - my); den += (xs[i] - mx) ** 2; }
    const slope = den ? num / den : 0, intercept = my - slope * mx, lastYear = parseInt(pts[n - 1].year, 10);
    data[data.length - 1] = { ...data[data.length - 1], projected: pts[n - 1].value };
    for (let k = 1; k <= 2; k++) data.push({ year: String(lastYear + k), projected: Math.max(0, Math.round(intercept + slope * (n - 1 + k))) });
    return { data, hasForecast: true };
  }, [trend]);

  // cumulative products per therapeutic area (top 5 ATC categories) over time
  const taCumulative = useMemo(() => {
    const areas = byAtc.slice(0, 5).map((a) => a.name);
    if (!areas.length) return { areas: [], data: [] };
    const years = [...new Set(products.map((r) => yearOf(firstDate(r))).filter(Boolean))].sort();
    const perYearArea = {};
    for (const r of products) {
      const y = yearOf(firstDate(r)); const a = r.atc_category;
      if (!y || !a || !areas.includes(a)) continue;
      (perYearArea[a] = perYearArea[a] || {})[y] = (perYearArea[a][y] || 0) + 1;
    }
    const running = {}; areas.forEach((a) => { running[a] = 0; });
    const data = years.map((y) => { const row = { year: String(y) }; areas.forEach((a) => { running[a] += (perYearArea[a]?.[y] || 0); row[a] = running[a]; }); return row; });
    return { areas, data };
  }, [products, byAtc]);

  const updatesByYear = useMemo(() => {
    const m = new Map();
    for (const r of products) { const y = yearOf(firstDate(r)); if (y) m.set(y, (m.get(y) || 0) + 1); }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([year, value]) => ({ year: String(year), value }));
  }, [products]);

  // molecule aggregation for red/orange tables
  const molAgg = useCallback((status) => {
    const m = new Map();
    for (const r of products) {
      if (statusOf(r) !== status) continue;
      const mol = r.inn || r.molecule_name; if (!mol) continue;
      if (!m.has(mol)) m.set(mol, { molecule: mol, products: 0, forms: new Set() });
      const e = m.get(mol); e.products += 1; if (r.dosage_form) e.forms.add(r.dosage_form);
    }
    return [...m.values()].map((e) => ({ molecule: e.molecule, products: e.products, forms: [...e.forms].join(', ') })).sort((a, b) => b.products - a.products);
  }, [products, statusOf]);
  const redRows = useMemo(() => molAgg('red'), [molAgg]);
  const orangeRows = useMemo(() => molAgg('orange'), [molAgg]);

  // Fetch Nova AI insights once data is ready
  useEffect(() => {
    if (loading || !products.length) return;
    // wait until switch-status has resolved (if the supplier has molecules) so red/orange counts are correct
    const molCount = new Set(products.map((r) => upper(r.inn || r.molecule_name)).filter(Boolean)).size;
    if (molCount > 0 && Object.keys(switchMap).length === 0) return;
    let alive = true;
    const summary = {
      headquarterCountry: info?.supplier_headquarter_country || null,
      products: kpis.products, molecules: kpis.molecules, dosageForms: kpis.forms,
      nlAvailable: kpis.nl, nlCoveragePct: products.length ? Math.round((kpis.nl / products.length) * 100) : 0,
      redListCount: kpis.red, orangeListCount: kpis.orange,
      topAtcCategories: byAtc.slice(0, 6), topTherapeuticAreas: byTA.slice(0, 8), topDosageForms: byForm.slice(0, 6),
      productsByYear: trend, cumulativeByYear: cumulative,
      projectedNextYears: predictive.hasForecast ? predictive.data.filter((d) => d.projected != null && d.actual == null) : [],
      redMolecules: redRows.slice(0, 15).map((r) => r.molecule),
      orangeMolecules: orangeRows.slice(0, 20).map((r) => r.molecule),
    };
    (async () => {
      setInsightsLoading(true);
      try {
        const token = await acquireToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/nova/cockpit-insights`, {
          method: 'POST', headers, body: JSON.stringify({ supplier: supplierName, summary }),
        });
        if (res.ok && alive) { const d = await res.json(); setInsights(d.insights || {}); }
        else if (alive) setInsights({});
      } catch { if (alive) setInsights({}); }
      finally { if (alive) setInsightsLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, products, switchMap, supplierName]);

  // Supplier news — async poll (web_search is slow)
  useEffect(() => {
    if (!supplierName) return;
    let alive = true;
    (async () => {
      setNewsStatus('loading'); setNews([]);
      for (let i = 0; i < 40 && alive; i++) {
        try {
          const token = await acquireToken();
          const headers = { 'Content-Type': 'application/json' };
          if (token) headers.Authorization = `Bearer ${token}`;
          const res = await fetch(`${import.meta.env.VITE_API_URL}/api/nova/supplier-news`, {
            method: 'POST', headers, body: JSON.stringify({ supplier: supplierName }),
          });
          if (res.ok) { const d = await res.json(); if (d.status === 'ready') { if (alive) { setNews(d.news || []); setNewsStatus('ready'); } return; } }
        } catch { /* retry */ }
        await new Promise((r) => setTimeout(r, 3000));
      }
      if (alive) setNewsStatus('ready');
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierName]);

  // table row datasets
  const productRows = useMemo(() => products.map((r) => ({
    inn: r.inn || r.molecule_name || '', product: r.supplier_product_name || '', form: r.dosage_form || '',
    strength: [r.strength_value_1, r.strength_unit_code_1].filter(Boolean).join(' '), atc: r.atc_code || '',
    cat: r.atc_category || '', nl: r.nl_market_available || '', status: statusOf(r),
  })), [products, statusOf]);
  const updateRows = useMemo(() => products.filter((r) => firstDate(r))
    .sort((a, b) => new Date(firstDate(b)) - new Date(firstDate(a))).slice(0, 60)
    .map((r) => ({ date: fmtDate(firstDate(r)), product: r.supplier_product_name || '', inn: r.inn || r.molecule_name || '', form: r.dosage_form || '', status: statusOf(r) })), [products, statusOf]);

  const badge = (s) => <span className={`cok-badge ${s}`}>{s}</span>;
  const productCols = [
    { key: 'inn', label: 'INN' }, { key: 'product', label: 'Product' },
    { key: 'form', label: 'Dosage form', type: 'select', options: formOptions },
    { key: 'strength', label: 'Strength' }, { key: 'atc', label: 'ATC code' },
    { key: 'cat', label: 'ATC category', type: 'select', options: atcOptions },
    { key: 'nl', label: 'NL', type: 'select', options: ['Yes', 'No'] },
    { key: 'status', label: 'Switch', type: 'select', options: ['red', 'orange', 'green'], render: (r) => badge(r.status) },
  ];
  const updateCols = [
    { key: 'date', label: 'Date' }, { key: 'product', label: 'Product' }, { key: 'inn', label: 'INN' },
    { key: 'form', label: 'Dosage form', type: 'select', options: formOptions },
    { key: 'status', label: 'Switch', type: 'select', options: ['red', 'orange', 'green'], render: (r) => badge(r.status) },
  ];
  const redCols = [{ key: 'molecule', label: 'Molecule (Red)' }, { key: 'products', label: 'Products', noFilter: true }, { key: 'forms', label: 'Dosage forms' }];
  const orangeCols = [{ key: 'molecule', label: 'Molecule (Orange)' }, { key: 'products', label: 'Products', noFilter: true }, { key: 'forms', label: 'Dosage forms' }];
  const TA_LINE = ['#1A8AAF', '#0D2B3E', '#1D8A5E', '#D98323', '#8E5AA8'];

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2600); };
  const exportPayload = () => ({
    supplier: supplierName, info, kpis, byForm, byAtc, byTA, nlGap, trend, cumulative,
    predictive, taCumulative, redRows, orangeRows, updateRows, updatesByYear,
    productRows, news, insights: insights || {},
  });
  const handleDownload = async (fmt) => {
    try {
      showToast(`Preparing ${fmt}…`);
      const payload = exportPayload();
      if (fmt === 'HTML') { exportCockpitHtml(payload); showToast('HTML downloaded.'); return; }
      if (fmt === 'PDF') { exportCockpitPdf(payload); showToast('Opening print dialog — choose “Save as PDF”.'); return; }
      const charts = await buildChartImages(payload);
      const token = await acquireToken();
      await exportCockpitBackend(fmt.toLowerCase(), payload, charts, token);
      showToast(`${fmt} downloaded.`);
    } catch { showToast(`${fmt} export failed.`); }
  };

  // Intro (≤2s) then a real progress bar gated on all Nova actions (data → switch-status → insights → news)
  const progressRef = useRef(3);
  const fadeLatch = useRef(false);
  useEffect(() => {
    const toLoading = setTimeout(() => setPhase((p) => (p === 'intro' ? 'loading' : p)), 2000);
    const failsafe = setTimeout(() => setPhase('done'), 120000); // never hang forever
    return () => { clearTimeout(toLoading); clearTimeout(failsafe); };
  }, []);
  useEffect(() => {
    if (phase === 'done' || phase === 'fading') return undefined;
    const id = setInterval(() => {
      const molCount = new Set(products.map((r) => upper(r.inn || r.molecule_name)).filter(Boolean)).size;
      const dataReady = !loading;
      const switchReady = !loading && (molCount === 0 || Object.keys(switchMap).length > 0);
      const insReady = insights !== null && !insightsLoading;
      const newsReady = newsStatus === 'ready';
      const all = dataReady && switchReady && insReady && newsReady;
      const target = 6 + (dataReady ? 24 : 0) + (switchReady ? 18 : 0) + (insReady ? 27 : 0) + (newsReady ? 24 : 0);
      const cap = (all && phase === 'loading') ? 100 : Math.min(target, 99);
      setProgress((prev) => { const v = prev >= cap ? cap : Math.min(cap, prev + Math.max(0.5, (cap - prev) * 0.06)); progressRef.current = v; return v; });
      if (all && phase === 'loading' && progressRef.current >= 99.9 && !fadeLatch.current) {
        fadeLatch.current = true;
        setPhase('fading');
        setTimeout(() => setPhase('done'), 450);
      }
    }, 90);
    return () => clearInterval(id);
  }, [phase, loading, products, switchMap, insights, insightsLoading, newsStatus]);

  if (err) return <div className="cok-page"><div className="cok-error">{err} <button className="cok-filter-reset" onClick={() => navigate(-1)}>Go back</button></div></div>;

  const chartTip = <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--brd)' }} />;

  return (
   <>
    {phase !== 'done' && (
      <div className={`cok-intro${phase === 'fading' ? ' fade' : ''}`}>
        <div className="cok-intro-welcome">Welcome to the Supplier Cockpit</div>
        <div className="cok-intro-sub">{supplierName}</div>
        {phase !== 'intro' && (
          <div className="cok-intro-barwrap">
            <div className="cok-intro-bar"><div className="cok-intro-fill" style={{ width: `${Math.round(progress)}%` }} /></div>
            <div className="cok-intro-pct">{Math.min(99, Math.round(progress)) === 99 && progress < 100 ? 'Finalising…' : `${Math.round(progress)}%`} · preparing your cockpit</div>
          </div>
        )}
      </div>
    )}
    <div className="cok-page">
      {/* Hero */}
      <div className="cok-hero">
        <div className="cok-welcome">Welcome to the Supplier Cockpit</div>
        <h1>{supplierName}</h1>
        <div className="cok-sub">{(info?.supplier_headquarter_country || '—')} · {products.length} products · {kpis.molecules} molecules</div>
        <div className="cok-actions">
          {['PDF', 'XLSX', 'PPTX', 'HTML', 'DOCX'].map((f) => (
            <button key={f} className="cok-dl-btn" onClick={() => handleDownload(f)}>Download {f}</button>
          ))}
        </div>
      </div>

      {/* Overview */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Overview</div>
        <div className="cok-kpis">
          <div className="cok-kpi"><div className="cok-kpi-lbl">Products</div><div className="cok-kpi-val">{kpis.products}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Molecules</div><div className="cok-kpi-val">{kpis.molecules}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Dosage forms</div><div className="cok-kpi-val">{kpis.forms}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">NL available</div><div className="cok-kpi-val green">{kpis.nl}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Red list</div><div className="cok-kpi-val red">{kpis.red}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Orange list</div><div className="cok-kpi-val orange">{kpis.orange}</div></div>
        </div>
      </div>

      {/* Summary */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Summary</div>
        <div className="cok-desc-grid">
          <div className="cok-desc-item"><div className="k">Headquarter country</div><div className="v">{info?.supplier_headquarter_country || '—'}</div></div>
          <div className="cok-desc-item"><div className="k">Approval status</div><div className="v">{info?.supplier_approval_status || '—'}</div></div>
          <div className="cok-desc-item"><div className="k">Account manager</div><div className="v">{[info?.account_manager, info?.account_manager2].filter(Boolean).join(', ') || '—'}</div></div>
          <div className="cok-desc-item"><div className="k">Contact</div><div className="v">{info?.contact_email_address || '—'}</div></div>
          <div className="cok-desc-item"><div className="k">Website</div><div className="v">{info?.supplier_url ? <a href={info.supplier_url} target="_blank" rel="noopener noreferrer">{info.supplier_url}</a> : '—'}</div></div>
          <div className="cok-desc-item"><div className="k">Newsroom</div><div className="v">{info?.supplier_newsroom ? <a href={info.supplier_newsroom} target="_blank" rel="noopener noreferrer">{info.supplier_newsroom}</a> : '—'}</div></div>
        </div>
      </div>

      {/* News (right under Summary) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />News</div>
        {info?.supplier_newsroom && (
          <div className="cok-news-room">Company newsroom: <a href={info.supplier_newsroom} target="_blank" rel="noopener noreferrer">{info.supplier_newsroom}</a></div>
        )}
        {newsStatus === 'loading' ? (
          <div className="cok-muted">Fetching the latest news…</div>
        ) : news.length === 0 ? (
          <div className="cok-muted">No recent news found for this supplier.</div>
        ) : (
          <div className="cok-news-list">
            {news.map((n, i) => (
              <div className="cok-news-item" key={i}>
                <div className="cok-news-top">
                  <span className="cok-news-title">{n.title}</span>
                  {n.date && <span className="cok-news-date">{n.date}</span>}
                </div>
                {n.summary && <div className="cok-news-sum">{n.summary}</div>}
                {n.url && <div className="cok-news-src"><a href={n.url} target="_blank" rel="noopener noreferrer">{n.source || 'Source'}</a></div>}
              </div>
            ))}
          </div>
        )}
        <div className="cok-muted" style={{ marginTop: 8, fontSize: 11 }}>News gathered via web search — verify against the linked sources.</div>
      </div>

      {/* Section 3 — AI insights */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />AI insights &amp; commentary</div>
        <AiComment text={insights?.overview} loading={insightsLoading} />
      </div>

      {/* Section 4 — Supplier portfolio evolution */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Supplier portfolio evolution</div>
        <div className="cok-charts">
          <div className="cok-chart-card" data-cok-chart="portfolioGrowth">
            <h4>Portfolio growth over time</h4>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={cumulative} margin={{ top: 4, right: 12, bottom: 4, left: -8 }}>
                <defs><linearGradient id="cokCum" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1A8AAF" stopOpacity={0.35} /><stop offset="100%" stopColor="#1A8AAF" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />{chartTip}
                <Area type="monotone" dataKey="cumulative" stroke="#1A8AAF" strokeWidth={2.5} fill="url(#cokCum)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="cok-chart-card" data-cok-chart="byAtc">
            <h4>Products by ATC category</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={byAtc} margin={{ top: 4, right: 8, bottom: 30, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />{chartTip}
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>{byAtc.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <AiComment text={insights?.portfolio} loading={insightsLoading} />
      </div>

      {/* Section 5 — Therapeutic area developments */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Therapeutic area developments</div>
        <div className="cok-charts one">
          <div className="cok-chart-card full" data-cok-chart="taCumulative">
            <h4>Cumulative products by therapeutic area</h4>
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={taCumulative.data} margin={{ top: 4, right: 16, bottom: 4, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />{chartTip}<Legend />
                {taCumulative.areas.map((a, i) => <Line key={a} type="monotone" dataKey={a} name={a} stroke={TA_LINE[i % TA_LINE.length]} strokeWidth={2} dot={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="cok-chart-card full" data-cok-chart="byTA">
            <h4>Products by therapeutic area (ATC classification)</h4>
            <ResponsiveContainer width="100%" height={Math.max(300, byTA.length * 30)}>
              <BarChart data={byTA} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={200} />{chartTip}
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>{byTA.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <AiComment text={insights?.therapeuticArea} loading={insightsLoading} />
      </div>

      {/* Section 6 — Predictive supplier developments (chart left, comment right) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Predictive supplier developments</div>
        <div className="cok-two">
          <div className="cok-chart-card" data-cok-chart="predictive">
            <h4>New products per year — actual &amp; projection {predictive.hasForecast ? '(next 2 years)' : ''}</h4>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={predictive.data} margin={{ top: 4, right: 12, bottom: 4, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />{chartTip}<Legend />
                <Line type="monotone" dataKey="actual" name="Actual" stroke="#0D2B3E" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="projected" name="Projected" stroke="#D98323" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <div className="cok-muted" style={{ marginTop: 6 }}>Projection is a simple linear trend of historical entries — indicative only.</div>
          </div>
          <AiComment text={insights?.predictive} loading={insightsLoading} />
        </div>
      </div>

      {/* Section 7 — NL market gap analysis */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />NL market gap analysis</div>
        <div className="cok-charts">
          <div className="cok-chart-card" data-cok-chart="nlGap">
            <h4>NL market availability</h4>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={nlGap} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={96} paddingAngle={2}>
                  {nlGap.map((d) => <Cell key={d.key} fill={STATUS_COLORS[d.key]} />)}
                </Pie>{chartTip}<Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="cok-chart-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h4>Availability summary</h4>
            <div className="cok-desc-grid">
              <div className="cok-desc-item"><div className="k">NL available</div><div className="v" style={{ color: 'var(--green)', fontWeight: 700 }}>{kpis.nl}</div></div>
              <div className="cok-desc-item"><div className="k">Not / unknown</div><div className="v" style={{ color: 'var(--red)', fontWeight: 700 }}>{products.length - kpis.nl}</div></div>
              <div className="cok-desc-item"><div className="k">Total products</div><div className="v" style={{ fontWeight: 700 }}>{products.length}</div></div>
              <div className="cok-desc-item"><div className="k">NL coverage</div><div className="v" style={{ fontWeight: 700 }}>{products.length ? Math.round((kpis.nl / products.length) * 100) : 0}%</div></div>
            </div>
          </div>
        </div>
        <AiComment text={insights?.nlGap} loading={insightsLoading} />
      </div>

      {/* Section 8 — Red list (chart left, table right) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Red list ({redRows.length})</div>
        {redRows.length === 0 ? <div className="cok-muted">No molecules on the red list.</div> : (
          <div className="cok-two">
            <div className="cok-chart-card" data-cok-chart="redBar">
              <h4>Top red-list molecules by products</h4>
              <ResponsiveContainer width="100%" height={Math.max(240, Math.min(redRows.length, 10) * 30)}>
                <BarChart data={redRows.slice(0, 10).map((r) => ({ name: r.molecule, value: r.products }))} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />{chartTip}
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#D64545" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <FilterableTable columns={redCols} rows={redRows} maxRows={200} emptyText="No matches." />
          </div>
        )}
      </div>

      {/* Section 9 — Orange list (chart left, table right) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Orange list ({orangeRows.length})</div>
        {orangeRows.length === 0 ? <div className="cok-muted">No molecules on the orange list.</div> : (
          <div className="cok-two">
            <div className="cok-chart-card" data-cok-chart="orangeBar">
              <h4>Top orange-list molecules by products</h4>
              <ResponsiveContainer width="100%" height={Math.max(240, Math.min(orangeRows.length, 10) * 30)}>
                <BarChart data={orangeRows.slice(0, 10).map((r) => ({ name: r.molecule, value: r.products }))} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />{chartTip}
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#D98323" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <FilterableTable columns={orangeCols} rows={orangeRows} maxRows={200} emptyText="No matches." />
          </div>
        )}
      </div>

      {/* Section 10 — Updates (chart, then table) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Updates received</div>
        <div className="cok-chart-card" data-cok-chart="updates" style={{ marginBottom: 14 }}>
          <h4>Updates per year</h4>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={updatesByYear} margin={{ top: 4, right: 8, bottom: 4, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />{chartTip}
              <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#1A8AAF" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {updateRows.length === 0 ? <div className="cok-muted">No dated updates.</div>
          : <FilterableTable columns={updateCols} rows={updateRows} maxRows={60} emptyText="No matches." />}
      </div>

      {/* Product list */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Product list ({products.length})</div>
        <FilterableTable columns={productCols} rows={productRows} paginate noun="products" emptyText="No matching products." />
      </div>

      {toast && <div className="cok-toast">{toast}</div>}
    </div>
   </>
  );
}
